/**
 * Vertical flow canvas that renders a DAG as stacked nodes with connectors.
 * Used in both the flow builder (editable) and template preview (read-only).
 */
import type { DagDefinition, DagNode, TriggerNodeConfig, ConditionGroupConfig, ActionBlockConfig } from '@/lib/dag-types';
import { TriggerNode } from './TriggerNode';
import { ConditionGroupNode } from './ConditionGroupNode';
import { ActionBlockNode } from './ActionBlockNode';
import { EndNode } from './EndNode';
import { FlowConnector } from './FlowConnector';

interface FlowCanvasProps {
  dag: DagDefinition;
  readOnly?: boolean;
  onNodeClick?: (nodeId: string) => void;
  onInsertNode?: (afterNodeId: string, type: 'condition_group' | 'action_block') => void;
}

/**
 * Build ordered node list from DAG edges (topological order via BFS from trigger).
 */
function getOrderedNodes(dag: DagDefinition): DagNode[] {
  const adjacency = new Map<string, string[]>();
  for (const node of dag.nodes) {
    adjacency.set(node.id, []);
  }
  for (const edge of dag.edges) {
    adjacency.get(edge.from)?.push(edge.to);
  }

  const trigger = dag.nodes.find((n) => n.type === 'trigger');
  if (!trigger) return dag.nodes;

  const ordered: DagNode[] = [];
  const visited = new Set<string>();
  const queue = [trigger.id];

  while (queue.length > 0) {
    const id = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    const node = dag.nodes.find((n) => n.id === id);
    if (node) ordered.push(node);
    for (const next of adjacency.get(id) ?? []) {
      queue.push(next);
    }
  }

  return ordered;
}

export function FlowCanvas({ dag, readOnly, onNodeClick, onInsertNode }: FlowCanvasProps) {
  const orderedNodes = getOrderedNodes(dag);
  let stepNum = 1;

  return (
    <div className="max-w-[580px] mx-auto py-4 space-y-0">
      {orderedNodes.map((node, i) => {
        const currentStep = stepNum;
        if (node.type !== 'end') stepNum++;

        return (
          <div key={node.id}>
            {node.type === 'trigger' && (
              <TriggerNode
                config={node.config as TriggerNodeConfig}
                readOnly={readOnly}
                onClick={() => onNodeClick?.(node.id)}
              />
            )}
            {node.type === 'condition_group' && (
              <ConditionGroupNode
                config={node.config as ConditionGroupConfig}
                stepNum={currentStep}
                readOnly={readOnly}
                onClick={() => onNodeClick?.(node.id)}
              />
            )}
            {node.type === 'action_block' && (
              <ActionBlockNode
                config={node.config as ActionBlockConfig}
                stepNum={currentStep}
                readOnly={readOnly}
                onClick={() => onNodeClick?.(node.id)}
              />
            )}
            {node.type === 'end' && <EndNode />}
            {i < orderedNodes.length - 1 && (
              <FlowConnector
                readOnly={readOnly}
                onInsert={
                  onInsertNode
                    ? (type) => onInsertNode(node.id, type)
                    : undefined
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
